'use client';

import { ExternalLinkIcon } from '@chakra-ui/icons';
import { IconProps } from '@chakra-ui/react';
import { isEmpty, snakeCase } from 'lodash';

import { RouteTripType } from '@/apis/gin/boundary-database/employee-commuting.dto';
import { EmissionSourceEquipments } from '@/app/org-cfv/src/constants/EquipmentConstants';
import { EmissionSourceProcesses } from '@/app/org-cfv/src/constants/ProcessConstants';
import {
  EmissionFactorByFactorErrorType,
  EmissionSourceCategory,
  EmissionSourceFactorType,
  EmissionSourceFactorUnitType,
  EmissionSourceGreenHouseGases,
  EmissionSourceRawMaterialOrProduct,
} from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { RawMaterialProducts } from '@/app/org-cfv/src/enums/RawMaterialProductEnum';
import { SideBarItem } from '@/components/common/Layout/sidebar/typings';
import { NextTFunction } from '@/i18n/client';
import { ROUTE_CONFIG } from '@/routes/constants';
import { Route } from '@/routes/enum';
import { PROJECT_ADMIN_ROLES, Role } from '@/typings/user';
import { isNumber } from '@/utils/validations';

import { PASSENGER_TRANS_MODES, RMT_MODE_MEAN_MAPPING } from '../factory-database/src/constants/rmt';
import { RMTLocationCategory } from '../factory-database/src/enums/rmt';
import EmissionSourceConstants from './src/constants/EmissionSourceConstants';
import { ActivityDataConfidenceType, ActivityDataType, ActivityUnit } from './src/enums/ActivityDataTypeEnum';

export const DEFAULT_SPACING = 4;
export const DEFAULT_GRID_COLUMN = 3;
export const READ_ONLY_UNIT_TYPE = EmissionSourceFactorUnitType.CURRENCY;

export const renderOrgCfvRoleSidebar = (
  t: NextTFunction,
  projectId: string,
  role: Role,
  isIdentified: boolean,
): SideBarItem[] => {
  if (isEmpty(projectId)) {
    return [];
  }

  if (PROJECT_ADMIN_ROLES.includes(role)) {
    return [
      {
        title: t('sidebar.emissionIdentification'),
        url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_EMISSION_IDENTIFICATION].getFullUrl({ projectId }),
        testId: snakeCase('emissionIdentification'),
      },
      {
        title: t('sidebar.sidebarEmission'),
        url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_EMISSION_SOURCES].getFullUrl({ projectId }),
        disabled: !isIdentified,
        testId: snakeCase('sidebarEmission'),
      },
      {
        title: t('sidebar.activityDataEntry'),
        url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_ACTIVITY_DATA_ENTRY].getFullUrl({ projectId }),
        disabled: !isIdentified,
        testId: snakeCase('activityDataEntry'),
      },
      {
        title: t('sidebar.activityDataApproval'),
        url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_ACTIVITY_DATA_APPROVAL].getFullUrl({ projectId }),
        disabled: !isIdentified,
        testId: snakeCase('activityDataApproval'),
      },
      {
        title: t('sidebar.dataCollection'),
        url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_DATA_COLLECTION].getFullUrl({ projectId }),
        disabled: !isIdentified,
        testId: snakeCase('dataCollection'),
      },
      {
        title: t('sidebar.dataQualityManagement'),
        url: ROUTE_CONFIG[Route.ORG_CFV_DATA_QUALITY_MGMT].getFullUrl({ projectId }),
        disabled: !isIdentified,
        testId: snakeCase('dataQualityManagement'),
      },
      {
        title: t('sidebar.uncertaintyQuantitativeAssessment'),
        url: ROUTE_CONFIG[Route.ORG_CFV_UNCERTAINTY_QUANTITATIVE_ASSESSMENT].getFullUrl({ projectId }),
        disabled: !isIdentified,
        testId: snakeCase('uncertaintyQuantitativeAssessment'),
      },
      {
        title: t('sidebar.dashboard'),
        url: ROUTE_CONFIG[Route.ORG_CFV_DASHBOARD].getFullUrl({ projectId }),
        disabled: !isIdentified,
        testId: snakeCase('dashboard'),
      },
      {
        title: t('sidebar.reportCenter'),
        url: ROUTE_CONFIG[Route.SINGLE_BOUNDARY_REPORT_EXPORT].getFullUrl(),
        disabled: !isIdentified,
        testId: snakeCase('reportCenter'),
        icon: (props: IconProps) => <ExternalLinkIcon color='blue.500' {...props} />,
      },
    ];
  } else if (role === Role.DATA_COLLECTOR) {
    return isIdentified
      ? [
          {
            title: t('sidebar.emissionIdentification'),
            url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_EMISSION_IDENTIFICATION].getFullUrl({ projectId }),
            testId: snakeCase('emissionIdentification'),
          },
          {
            title: t('sidebar.sidebarEmission'),
            url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_EMISSION_SOURCES].getFullUrl({ projectId }),
            disabled: false,
            testId: snakeCase('sidebarEmission'),
          },
          {
            title: t('sidebar.activityDataEntry'),
            url: ROUTE_CONFIG[Route.ORG_CFV_PROJECT_ACTIVITY_DATA_ENTRY].getFullUrl({ projectId }),
            disabled: false,
            testId: snakeCase('activityDataEntry'),
          },
          {
            title: t('sidebar.dataQualityManagement'),
            url: ROUTE_CONFIG[Route.ORG_CFV_DATA_QUALITY_MGMT].getFullUrl({ projectId }),
            disabled: false,
            testId: snakeCase('dataQualityManagement'),
          },
          {
            title: t('sidebar.uncertaintyQuantitativeAssessment'),
            url: ROUTE_CONFIG[Route.ORG_CFV_UNCERTAINTY_QUANTITATIVE_ASSESSMENT].getFullUrl({ projectId }),
            disabled: false,
            testId: snakeCase('uncertaintyQuantitativeAssessment'),
          },
          {
            title: t('sidebar.dashboard'),
            url: ROUTE_CONFIG[Route.ORG_CFV_DASHBOARD].getFullUrl({ projectId }),
            disabled: false,
            testId: snakeCase('dashboard'),
          },
        ]
      : [];
  } else {
    return [];
  }
};

export const isGhgFactorValid = (value: string | number | null | undefined) => {
  if (!isNumber(value) || typeof value?.toString() == 'undefined') {
    return false;
  }

  const numStr = value?.toString();
  if (!numStr.includes('.')) {
    return true;
  } else if (numStr.endsWith('.')) {
    return false;
  }

  const numStrSplit = numStr.split('.');
  if (numStrSplit.length < 2) {
    return false;
  } else if (numStrSplit[1].length > 12) {
    // The browser will process one more digit when judging the value. So it's > 12
    return false;
  }

  return true;
};

export const generateEquipmentOptions = (t: NextTFunction) =>
  EmissionSourceEquipments.map((equipment) => ({
    label: `${equipment} - ${t(`equipment.${equipment}`)}`,
    value: equipment,
  }));

export const generateProcessOptions = (t: NextTFunction) =>
  EmissionSourceProcesses.map((process) => ({
    label: `${process} - ${t(`process.${process}`)}`,
    value: process,
  }));

export const generateRawMaterialOneOptions = (category: EmissionSourceCategory, t: NextTFunction) => {
  if (category !== EmissionSourceCategory.CAT_2) {
    return [];
  }

  return [
    {
      label: t(`org-cfv-create-emission.rawMaterialProduct-${EmissionSourceRawMaterialOrProduct.RAW_MATERIAL}`),
      value: EmissionSourceRawMaterialOrProduct.RAW_MATERIAL,
    },
    {
      label: t(`common.rawMaterialProduct-${EmissionSourceRawMaterialOrProduct.PRODUCT}`),
      value: EmissionSourceRawMaterialOrProduct.PRODUCT,
    },
  ];
};

export const generateRawMaterialOrProductOptions = (t: NextTFunction) =>
  Object.keys(EmissionSourceRawMaterialOrProduct).map((rawMaterial) => ({
    value: rawMaterial,
    label: t(`org-cfv-create-emission.rawMaterialProduct-${rawMaterial}`),
  }));

export const generateRawMaterialProductOptions = (t: NextTFunction) =>
  Object.keys(RawMaterialProducts).map((rawMaterial) => ({
    value: rawMaterial,
    label: `${rawMaterial.replace('RM_', '')} - ${t(`raw-material-product.${rawMaterial}`)}`,
  }));

export const generateActivityUnitOptions = (factorUnitType: EmissionSourceFactorUnitType, t: NextTFunction) => {
  const units = EmissionSourceConstants.EMISSION_FACTOR_TYPE_TO_ACTIVITY_UNIT_DROPDOWN_OPTIONS[factorUnitType];

  if (!units) {
    return [];
  }

  return units.map((activityUnit) => ({
    value: activityUnit,
    label: t(`unit.${activityUnit}`),
  }));
};

export const generateActivityOptionsByActivityUnit = (unit: ActivityUnit, t: NextTFunction) => {
  if (!unit) {
    return [];
  }

  const unitType = getUnitTypeByActivityUnit(unit);

  if (!unitType) {
    return [];
  }

  return generateActivityUnitOptions(unitType, t);
};

export const generateCustomizedFactorSourceOptions = (t: NextTFunction) => ({
  value: EmissionSourceFactorType.CUSTOMIZED,
  label: t('org-cfv-create-emission.customizedEmissionFactor'),
});

export const generateActivityDataTypeOptions = (t: NextTFunction) =>
  Object.values(ActivityDataType)
    .filter((dataType) => dataType !== ActivityDataType.NONE)
    .map((dataType) => ({
      value: dataType,
      label: t(`org-cfv-data-entry.activityDataType-${dataType}`),
    }));

export const generateActivityDataConfidenceTypeOptions = (t: NextTFunction) =>
  Object.values(ActivityDataConfidenceType)
    .filter((dataConfidenceType) => dataConfidenceType !== ActivityDataConfidenceType.NONE)
    .map((dataConfidenceType) => ({
      value: dataConfidenceType,
      label: t(`org-cfv-data-entry.${dataConfidenceType}`),
    }));

export const generateCustomizedFactorOptions = (t: NextTFunction) =>
  Object.values(EmissionFactorByFactorErrorType).map((factor) => ({
    value: factor,
    label: t(`org-cfv-create-emission.factorErrorType-${factor}`),
  }));

// hook version: src/hooks/useUnitTypeOptions.ts
export const generateUnitTypeOptions = (
  t: NextTFunction,
  unitTypes: EmissionSourceFactorUnitType[] = Object.values(EmissionSourceFactorUnitType),
) =>
  unitTypes.map((unitType) => ({
    value: unitType,
    label: t(`unit.${unitType}`),
  }));

export const getUnitTypeByActivityUnit = (unit?: ActivityUnit | null): EmissionSourceFactorUnitType | undefined => {
  if (!unit) {
    return;
  }

  return Object.keys(EmissionSourceConstants.EMISSION_FACTOR_TYPE_TO_ACTIVITY_UNIT_DROPDOWN_OPTIONS).find(
    (unitTypeString) => {
      const unitType = unitTypeString as EmissionSourceFactorUnitType;

      if (
        EmissionSourceConstants.EMISSION_FACTOR_TYPE_TO_ACTIVITY_UNIT_DROPDOWN_OPTIONS[unitType].find((u) => u === unit)
      ) {
        return unitTypeString;
      }
    },
  ) as EmissionSourceFactorUnitType;
};

export const generateGhgOptions = (
  t: NextTFunction,
  ghgs: EmissionSourceGreenHouseGases[] = Object.values(EmissionSourceGreenHouseGases).filter(
    (ghg) => ghg !== EmissionSourceGreenHouseGases.NA,
  ),
) =>
  ghgs.map((ghg) => ({
    value: ghg,
    label: t(`ghg.${ghg}`),
  }));

export const generateRmtPassengerMeansOptions = (t: NextTFunction) =>
  PASSENGER_TRANS_MODES.map((mode) => RMT_MODE_MEAN_MAPPING[mode])
    .flat()
    .map((mean) => ({
      value: mean,
      label: t(`fd-rmt.${mean}`),
    }));

export const generateLocationCategoryOptionsWithout05And06 = (t: NextTFunction) =>
  Object.values(RMTLocationCategory)
    .filter((c) => ![RMTLocationCategory.LOCATION_CAT_05, RMTLocationCategory.LOCATION_CAT_06].includes(c))
    .map((category) => ({
      value: category,
      label: t(`fd-rmt.${category}`),
    }));

export const generateRouteTripTypeOptions = (t: NextTFunction) => [
  {
    value: RouteTripType.OnewayTrip,
    label: t('common.oneway'),
  },
  {
    value: RouteTripType.RoundTrip,
    label: t('common.roundTrip'),
  },
];

export const isActivityUnitDisabled = (unit: ActivityUnit | null | undefined) => {
  if (!unit) {
    return false;
  }

  return getUnitTypeByActivityUnit(unit) === READ_ONLY_UNIT_TYPE;
};
